import { baseClient } from 'builder/modules/apiClient'
import { createDeferredPromise } from '../../utils/createDeferredPromise'

export class WebsocketSpeechRecognition {
  transcription = ''
  speechRecognition: SpeechRecognition
  error = false
  mediaRecorder: MediaRecorder
  ws: WebSocket
  stream: MediaStream
  audioContext: AudioContext
  completePromise: ReturnType<typeof createDeferredPromise>

  constructor(stream: MediaStream) {
    this.stream = stream
  }

  async setupAudioContext() {
    this.audioContext = new AudioContext({
      sampleRate: 48000,
      latencyHint: 'interactive',
    })

    const source = this.audioContext.createMediaStreamSource(this.stream)
    const processorUrl = /* webpackChunkName: "audio-processor.worklet" */ new URL(
      './audio-processor.worklet.js',
      import.meta.url,
    )

    await this.audioContext.audioWorklet.addModule(processorUrl)
    const processor = new AudioWorkletNode(this.audioContext, 'audio-chunk-processor')
    source.connect(processor)
    processor.connect(this.audioContext.destination)
    return processor
  }

  async #start() {
    const [processor, response] = await Promise.all([
      this.setupAudioContext(),
      baseClient.get<{ url: string }>('/interview_prep/ws/presigned_url'),
    ])

    this.ws = new WebSocket(response.data.url)

    this.completePromise = createDeferredPromise()

    this.ws.onmessage = message => {
      try {
        const event = JSON.parse(message.data)
        if (event.notification === 'transcription') {
          this.transcription += event.transcription
        }
        if (event.notification === 'server_end') {
          this.completePromise.resolve(true)
        }
      } catch (error) {
        console.error(error)
      }
    }

    processor.port.onmessage = async event => {
      const wavData = new Uint8Array(event.data)
      if (this.ws.readyState === this.ws.OPEN) {
        const binaryString = String.fromCharCode.apply(null, wavData as any)
        const encodedData = btoa(binaryString)

        this.ws.send(
          JSON.stringify({
            notification: 'send_audio',
            audio_data: encodedData,
          }),
        )
      }
    }
  }

  start() {
    this.transcription = ''
    return this.#start()
  }

  async #stop() {
    if (this.audioContext.state !== 'closed') {
      await this.audioContext.close()
    }

    if (this.ws.readyState === this.ws.OPEN) {
      this.ws.send(
        JSON.stringify({
          notification: 'client_end',
        }),
      )
    }

    return this.completePromise.promise
  }

  pause() {
    this.#stop()
  }

  resume() {
    return this.#start()
  }

  async stop() {
    await this.#stop()
    return this.transcription
  }
}
